import { useState } from "react";
import DropdownMenu from "../../ui/DropdownMenu";

const PRODUCT_UOM_OPTIONS = [
  { value: "pcs", label: "Pieces (pcs)" },
  { value: "nos", label: "Numbers (nos)" },
  { value: "kg", label: "Kilograms (kg)" },
  { value: "gm", label: "Grams (gm)" },
  { value: "ltr", label: "Liters (ltr)" },
  { value: "ml", label: "Milliliters (ml)" },
  { value: "mtr", label: "Meters (mtr)" },
  { value: "ft", label: "Feet (ft)" },
  { value: "sqft", label: "Square Feet (sqft)" },
  { value: "box", label: "Box" },
  { value: "pack", label: "Pack" },
  { value: "set", label: "Set" },
  { value: "unit", label: "Unit" },
];

const SERVICE_UOM_OPTIONS = [
  { value: "hrs", label: "Hour" },
  { value: "days", label: "Day" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
  { value: "quarter", label: "Quarter" },
  { value: "year", label: "Year" },
  { value: "task", label: "Task" },
  { value: "project", label: "Project" },
  { value: "session", label: "Session" },
  { value: "unit", label: "Unit" },
];

const BILLING_FREQUENCY_OPTIONS = [
  { value: "one_time", label: "One-time" },
  { value: "per_hour", label: "Per Hour" },
  { value: "per_day", label: "Per Day" },
  { value: "per_week", label: "Per Week" },
  { value: "per_month", label: "Per Month" },
  { value: "per_quarter", label: "Per Quarter" },
  { value: "per_year", label: "Per Year" },
];

export default function ItemForm({ form, setForm }) {
  const isProduct = form.type === "product";

  const [uomAnchor, setUomAnchor] = useState(null);
  const [billingAnchor, setBillingAnchor] = useState(null);
  const [taxAnchor, setTaxAnchor] = useState(null);
  const uomDropdown = useDropdown();
  const billingDropdown = useDropdown();

  // Get appropriate UOM options based on item type
  const uomOptions = isProduct ? PRODUCT_UOM_OPTIONS : SERVICE_UOM_OPTIONS;

  function useDropdown() {
    const [open, setOpen] = useState(false);
    const [anchor, setAnchor] = useState(null);

    function toggle(e) {
      const rect = e.currentTarget.getBoundingClientRect();
      setAnchor(rect);
      setOpen(true);
    }

    function close() {
      setOpen(false);
      setAnchor(null);
    }

    return { open, anchor, toggle, close };
  }

  return (
    <div className="space-y-8 text-sm">
      {/* ───────── Item Type ───────── */}
      <section className="space-y-3">
        <h3 className="section-title">Item type</h3>

        <div className="flex gap-2">
          {["product", "service"].map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => {
                const updates = { type };
                // Set appropriate defaults when switching type
                if (type === "product") {
                  updates.uom = form.uom || "pcs";
                  updates.billingFrequency = null; // Clear billing frequency for products
                } else {
                  updates.uom = form.uom || "hrs";
                  updates.billingFrequency = form.billingFrequency || "per_month"; // Default billing frequency
                }
                setForm({ ...form, ...updates });
              }}
              className={`
                    px-4 py-2 rounded-lg border text-sm transition
                    ${
                      form.type === type
                        ? "bg-[var(--color-primary)] text-white border-transparent"
                        : "bg-white hover:bg-[var(--color-bg-muted)]"
                    }
                `}
            >
              {type === "product" ? "Product" : "Service"}
            </button>
          ))}
        </div>
      </section>

      {/* ───────── Basic Info ───────── */}
      <section className="space-y-4">
        <h3 className="section-title">Basic information</h3>

        <input
          placeholder={isProduct ? "Product name" : "Service name"}
          className="input"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />

        <textarea
          placeholder="Description (optional)"
          rows={3}
          className="input resize-none"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
        />
      </section>

      {/* ───────── Classification ───────── */}
      <section className="space-y-4">
        <h3 className="section-title">
          {isProduct ? "Product classification" : "Service classification"}
        </h3>

        <div className="grid grid-cols-2 gap-3">
          <input
            placeholder={isProduct ? "HSN code" : "SAC code"}
            className="input"
            value={isProduct ? form.hsn : form.sac}
            onChange={(e) =>
              setForm({
                ...form,
                [isProduct ? "hsn" : "sac"]: e.target.value,
              })
            }
          />

          <div className="relative">
            <button
              type="button"
              onClick={uomDropdown.toggle}
              className="
      input
      flex items-center justify-between
      text-left
    "
            >
              <span>
                {uomOptions.find((u) => u.value === form.uom)?.label ||
                  "Unit of measurement"}
              </span>
              <span className="text-gray-400">▾</span>
            </button>

            {uomDropdown.open && (
              <DropdownMenu
                anchorRect={uomDropdown.anchor}
                onClose={uomDropdown.close}
              >
                <div
                  className="
          max-h-48
          overflow-y-auto
        "
                >
                  {uomOptions.map((uom) => (
                    <button
                      key={uom.value}
                      onClick={() => {
                        setForm({ ...form, uom: uom.value });
                        uomDropdown.close();
                      }}
                      className={`
              w-full
              px-3 py-2
              text-left
              hover:bg-[var(--color-bg-muted)]
              ${
                form.uom === uom.value
                  ? "text-[var(--color-primary)] font-medium"
                  : ""
              }
            `}
                    >
                      {uom.label}
                    </button>
                  ))}
                </div>
              </DropdownMenu>
            )}
          </div>
        </div>

        {/* Billing Frequency - Only for services */}
        {!isProduct && (
          <div className="relative">
            <label className="block text-xs text-[var(--color-text-muted)] mb-2">
              Billing Frequency
            </label>
            <button
              type="button"
              onClick={billingDropdown.toggle}
              className="
                input
                flex items-center justify-between
                text-left
                w-full
              "
            >
              <span>
                {BILLING_FREQUENCY_OPTIONS.find((b) => b.value === form.billingFrequency)?.label ||
                  "Select billing frequency"}
              </span>
              <span className="text-gray-400">▾</span>
            </button>

            {billingDropdown.open && (
              <DropdownMenu
                anchorRect={billingDropdown.anchor}
                onClose={billingDropdown.close}
              >
                <div className="max-h-48 overflow-y-auto">
                  {BILLING_FREQUENCY_OPTIONS.map((billing) => (
                    <button
                      key={billing.value}
                      onClick={() => {
                        setForm({ ...form, billingFrequency: billing.value });
                        billingDropdown.close();
                      }}
                      className={`
                        w-full
                        px-3 py-2
                        text-left
                        hover:bg-[var(--color-bg-muted)]
                        ${
                          form.billingFrequency === billing.value
                            ? "text-[var(--color-primary)] font-medium"
                            : ""
                        }
                      `}
                    >
                      {billing.label}
                    </button>
                  ))}
                </div>
              </DropdownMenu>
            )}
          </div>
        )}

        <p className="text-xs text-[var(--color-text-muted)]">
          {isProduct
            ? "HSN is required for GST invoices on goods"
            : "SAC is required for GST invoices on services"}
        </p>
      </section>

      {/* ───────── Pricing ───────── */}
      <section className="space-y-4">
        <h3 className="section-title">Pricing</h3>

        <div className="grid grid-cols-2 gap-3">
          <input
            type="number"
            placeholder="Unit price (₹)"
            className="input"
            value={form.price}
            onChange={(e) => setForm({ ...form, price: e.target.value })}
          />

          {/* Tax dropdown trigger */}
          <button
            type="button"
            onClick={(e) =>
              setTaxAnchor(e.currentTarget.getBoundingClientRect())
            }
            className="input flex justify-between items-center"
          >
            <span
              className={
                form.tax !== "" ? "" : "text-[var(--color-text-muted)]"
              }
            >
              {form.tax !== "" ? `${form.tax}% GST` : "Tax"}
            </span>
            <span className="text-xs">▾</span>
          </button>

          {taxAnchor && (
            <DropdownMenu
              anchorRect={taxAnchor}
              onClose={() => setTaxAnchor(null)}
            >
              {["0", "5", "12", "18"].map((rate) => (
                <button
                  key={rate}
                  onClick={() => {
                    setForm({ ...form, tax: rate });
                    setTaxAnchor(null);
                  }}
                  className={`
          w-full px-3 py-2 text-left
          hover:bg-[var(--color-bg-muted)]
          ${form.tax === rate ? "text-[var(--color-primary)] font-medium" : ""}
        `}
                >
                  {rate}% GST
                </button>
              ))}
            </DropdownMenu>
          )}
        </div>
      </section>

      {/* ───────── Additional ───────── */}
      <section className="space-y-4 pb-6">
        <h3 className="section-title">Additional details</h3>

        <textarea
          placeholder="Internal notes (optional)"
          rows={2}
          className="input resize-none"
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
        />
      </section>
    </div>
  );
}
